import React from 'react'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import { FormReadonlyField } from '@/components/Form/FormField'
import { PlatformStatisticsDto } from 'api/Trading-Accounts/Types/PlatformStatisticsDto'
import { formatLocalDateUTC, formatUTCDate } from 'utils/date'

interface Props {
  platformStatistics: PlatformStatisticsDto
}

interface StatisticField {
  label: string
  value: React.ComponentProps<typeof FormReadonlyField>['value']
}

const formatStatisticDate = (value?: string | null) => formatLocalDateUTC(value) || ''

const renderStatisticCard = (title: string, fields: StatisticField[], columns: 1 | 2 = 2) => (
  <DataCard title={title} columns={columns}>
    {fields.map((field) => (
      <FormReadonlyField key={field.label} label={field.label} value={field.value} />
    ))}
  </DataCard>
)

const TradingAccountStatisticsTable: React.FC<Props> = ({ platformStatistics }) => {
  const mainFields: StatisticField[] = [
    {
      label: 'Platform',
      value: platformStatistics.platformType.name || '',
    },
    {
      label: 'Last Login',
      value: platformStatistics.lastDate
        ? (formatUTCDate(platformStatistics.lastDate, true) ?? '')
        : '',
    },
    {
      label: 'Currency',
      value: platformStatistics.currency.id || '',
    },
    ...(platformStatistics.status
      ? [
          {
            label: 'Status',
            value: platformStatistics.status.name || '',
          },
        ]
      : []),
    {
      label: 'Balance',
      value: platformStatistics.balance,
    },
    {
      label: 'Equity',
      value: platformStatistics.equity,
    },
    {
      label: 'Used Margin',
      value: platformStatistics.usedMargin,
    },
    {
      label: 'Free Margin',
      value: platformStatistics.freeMargin,
    },
    {
      label: 'Margin Level',
      value: platformStatistics.marginLevel,
    },
    {
      label: 'Commissions Level',
      value: platformStatistics.totalCommissions,
    },
    {
      label: 'Swaps',
      value: platformStatistics.totalSwaps,
    },
  ]

  const depositFields: StatisticField[] = [
    {
      label: 'Total Number',
      value: platformStatistics.totalNumberOfDeposits,
    },
    {
      label: 'Total Amount',
      value: platformStatistics.totalDepositAmount,
    },
    {
      label: 'First',
      value: formatStatisticDate(platformStatistics.firstDeposit),
    },
    {
      label: 'Last',
      value: formatStatisticDate(platformStatistics.lastDeposit),
    },
  ]

  const withdrawalFields: StatisticField[] = [
    {
      label: 'Total Number',
      value: platformStatistics.totalNumberOfWithdrawals,
    },
    {
      label: 'Total Amount',
      value: platformStatistics.totalWithdrawalAmount,
    },
    {
      label: 'First',
      value: formatStatisticDate(platformStatistics.firstWithdrawal),
    },
    {
      label: 'Last',
      value: formatStatisticDate(platformStatistics.lastWithdrawal),
    },
  ]

  const closedTradeFields: StatisticField[] = [
    {
      label: 'Number',
      value: platformStatistics.totalNumberOfClosedTrades,
    },
    {
      label: 'Lots',
      value: platformStatistics.totalClosedLots,
    },
    {
      label: 'Profit',
      value: platformStatistics.totalClosedProfit,
    },
  ]

  const openTradeFields: StatisticField[] = [
    {
      label: 'Number',
      value: platformStatistics.totalNumberOfOpenTrades,
    },
    {
      label: 'Lots',
      value: platformStatistics.totalOpenLots,
    },
    {
      label: 'Profit',
      value: platformStatistics.totalOpenProfit,
    },
  ]

  const tradeDateFields: StatisticField[] = [
    {
      label: 'First Trade',
      value: formatStatisticDate(platformStatistics.firstTrade),
    },
    {
      label: 'Last Trade',
      value: formatStatisticDate(platformStatistics.lastTrade),
    },
  ]

  return (
    <DataGrid>
      <DataColumn>
        {renderStatisticCard('Main', mainFields)}
        {renderStatisticCard('Deposits', depositFields)}
        {renderStatisticCard('Withdrawals', withdrawalFields)}
      </DataColumn>
      <DataColumn>
        {renderStatisticCard('Closed Trades', closedTradeFields)}
        {renderStatisticCard('Open Trades', openTradeFields)}
        {renderStatisticCard('Trade Dates', tradeDateFields, 1)}
      </DataColumn>
    </DataGrid>
  )
}

export default TradingAccountStatisticsTable
